// Modules
import React, { useState, useEffect, useContext, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHistory } from 'react-router-dom';

// Components
import { ButtonPrimary, Loading, NoResults, Table } from '../../../common/components';

// Context
import { MeterUpgradeProcessContext } from '../meterUpradeProcess/meterUpgradeProcessContext';

// Styles
import { ChooseSiteWrapper } from './ChooseSite.styles';

// Api
import { globalApiParams } from '../../../common/api/globals';

const headers: Array<Common.HeaderItem> = [
  {
    title: 'Site reference',
    dataValue: 'site-reference',
    width: 150,
    enableSort: false,
  },
  {
    title: 'Site address',
    dataValue: 'site-address',
    minWidth: 140,
    enableSort: false,
  },
];

type ChooseSiteComponentProps = {
  selectedType: string;
  gridData: any[];
  handleCheckedRow: Function;
  handlePagingChange: any;
  isLoading?: boolean;
  hasMoreData: boolean;
  currentSortField: string;
  currentSortOrder: 'ASC' | 'DESC' | '';
};

const defaultProps = {
  isLoading: true,
  gridData: [],
  selectedType: 'Electricity',
  hasMoreData: true,
  currentSortField: 'essSiteID',
  currentSortOrder: 'DESC',
};

export const ChooseSite = (props: ChooseSiteComponentProps) => {
  const history = useHistory();
  const { gridData, selectedType, isLoading } = props;
  const { setSelectedSites } = useContext<any>(MeterUpgradeProcessContext);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [isAllSelected, setSelectAll] = useState<boolean>(false);

  const scrollableTable = useRef<any>();

  const sortDetails: Common.ISort = { field: props.currentSortField, order: props.currentSortOrder };

  const scrollToTop = () => {
    if (scrollableTable.current) {
      scrollableTable.current.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    if (props.gridData.length <= globalApiParams.pageSize) {
      scrollToTop();
    }
  }, [props.gridData]);

  useEffect(() => {
    clearSelection();
  }, [selectedType]);

  const clearSelection = () => {
    setSelectedRow(null);
    setCheckedItems([]);
  };

  const actionStyles = {
    width: '150px',
    paddingLeft: '0',
  };

  const handleSelectAll = () => {
    setSelectedRow(null);

    const select = !isAllSelected;
    setSelectAll(select);
    if (select) {
      const items: Array<number> = props.gridData.map((item, index) => index);
      setCheckedItems(items);
    } else {
      // unselect
      setCheckedItems([]);
    }
  };

  const isChecked = (index: any) => {
    const foundIndex = checkedItems.findIndex((checkedIndex: number) => checkedIndex === index);
    return foundIndex > -1;
  };

  const handleCheckRow = (index: number) => {
    setSelectedRow(null);

    const items = checkedItems.slice(0, checkedItems.length);

    const foundIndex = items.findIndex((checkedIndex: number) => checkedIndex === index);

    if (foundIndex > -1) {
      items.splice(foundIndex, 1);
    } else {
      items.push(index);
    }

    if (items.length === props.gridData.length) {
      handleSelectAll();
    } else if (isAllSelected) {
      setSelectAll(false);
    }

    setCheckedItems(items);
  };

  const getSelectedSites = (indexes: number[]) => {
    return gridData.filter((_item: any, index: number) => indexes.includes(index));
  };

  const handleNextClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    const sitesToProceed = getSelectedSites(checkedItems);
    setSelectedSites(sitesToProceed);

    history.push('meter-upgrade/contactdetails');
  };

  const handlePagingChange = () => {
    props.handlePagingChange();
  };

  return (
    <ChooseSiteWrapper>
      {!isLoading && gridData.length === 0 && <NoResults />}

      <div className='nextButton'>
        <ButtonPrimary
          title='Next'
          className='m-0'
          disabled={checkedItems.length == 0}
          onClick={(event: any) => handleNextClick(event)}
        />
      </div>

      {gridData.length > 0 && (
        <>
          <InfiniteScroll
            dataLength={props.gridData.length}
            next={handlePagingChange}
            hasMore={props.hasMoreData}
            loader={''}
            scrollableTarget='scrollableDiv'
            scrollThreshold={0.9}
          >
            <div
              id='scrollableDiv'
              ref={scrollableTable}
              className='table-responsive table-fixed fixed-column columns-1'
            >
              <Table
                isSelectable={true}
                headers={headers}
                rows={props.gridData}
                isChecked={isChecked}
                sortDetails={sortDetails}
                onClickSort={() => {}}
                actionStyles={actionStyles}
                isAllSelected={isAllSelected}
                selectedRowIndex={selectedRow}
                handleSelectAll={handleSelectAll}
                handleCheckRow={handleCheckRow}
                handleSelectRow={() => {}}
                className='list table plain text-left'
              />
            </div>
          </InfiniteScroll>
        </>
      )}

      {isLoading && <Loading overlay />}
    </ChooseSiteWrapper>
  );
};

ChooseSite.defaultProps = defaultProps;
